import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, FileText } from "lucide-react";
import { api, exportUrl } from "@/lib/api";

const Stat = ({ label, value }) => (
  <div className="min-w-[88px]"><div className="label-xs">{label}</div><div className="text-sm font-semibold text-slate-100 mt-0.5">{value ?? "-"}</div></div>
);

function stats(s) {
  return [
    ["JDs used", s.stage1?.usable], ["Tier 1 / keywords", `${s.stage2?.must_haves}/${s.stage2?.keywords}`],
    ["Coverage", s.stage5 ? `${s.stage5.coverage_pct}%` : null], ["Panel low", s.stage6?.scores ? Math.min(...Object.values(s.stage6.scores)) : null],
    ["ATS match", s.stage7 ? `${s.stage7.achievable_pct ?? s.stage7.match_pct}%` : null], ["vs all JD keywords", s.stage7 ? `${s.stage7.match_pct}%` : null], ["JD median", s.stage7?.median != null ? `${s.stage7.median}%` : null],
    ["JDs 75%+", s.stage7?.ge75_pct != null ? `${s.stage7.ge75_pct}%` : null], ["Title match", s.stage7?.title_match_pct != null ? `${s.stage7.title_match_pct}%` : null],
    ["Boolean", s.stage7 ? `${s.stage7.booleans_passed}/${s.stage7.booleans}` : null],
    ["AI-tell", s.stage8 ? `${s.stage8.before_score}→${s.stage8.after_score}%` : null], ["Untraceable", s.stage9?.untraceable],
  ];
}

function Row({ r }) {
  const [open, setOpen] = useState(false);
  const must = r.keywords.filter((k) => k.must_have);
  return (
    <div className="card-surface" data-testid={`result-${r.job_id}`}>
      <button className="w-full flex items-center gap-4 text-left" onClick={() => setOpen(!open)} data-testid={`result-toggle-${r.job_id}`}>
        <div className="flex-1 min-w-0">
          <div className="font-semibold font-display">{r.client_name} <span className="text-slate-400 font-normal">· {r.track_title}</span></div>
          <div className="text-xs text-slate-400 mt-0.5">Approved by {r.approval?.approved_by} on {r.approval?.date} · {r.headline}</div>
        </div>
        <div className="hidden xl:flex gap-4">{stats(r.scores).slice(2, 6).map(([l, v]) => <Stat key={l} label={l} value={v} />)}</div>
        <ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-4 pt-4 border-t border-border space-y-4 fade-in">
          <div className="flex flex-wrap gap-5">{stats(r.scores).map(([l, v]) => <Stat key={l} label={l} value={v} />)}</div>
          <div><div className="label-xs mb-1.5">Tier 1 keywords ({must.length})</div>
            <div className="flex flex-wrap gap-1.5">{must.map((k) => <span key={k.keyword} className="text-xs px-2 py-0.5 rounded border border-blue-500/40 text-blue-200">{k.keyword} · {k.frequency}%</span>)}</div></div>
          <div><div className="label-xs mb-1.5">Top keywords</div>
            <div className="flex flex-wrap gap-1.5">{r.keywords.filter((k) => !k.must_have).slice(0, 30).map((k) => <span key={k.keyword} className="text-xs px-2 py-0.5 rounded border border-border text-slate-300">{k.keyword} · {k.frequency}%</span>)}</div></div>
          <div className="text-xs text-slate-400">{r.roles.map((x) => `${x.title} at ${x.company} (${x.bullets} bullets)`).join(" · ")}</div>
          <div className="flex gap-2">
            <Link to={`/jobs/${r.job_id}`} data-testid={`result-open-${r.job_id}`} className="text-xs px-3 py-1.5 rounded-md border border-border hover:border-blue-500/60">Open client</Link>
            {["docx", "pdf"].map((k) => <a key={k} href={exportUrl(r.job_id, k)} data-testid={`result-${k}-${r.job_id}`} className="text-xs px-3 py-1.5 rounded-md border border-border hover:border-blue-500/60 inline-flex items-center gap-1"><FileText size={12} />{k.toUpperCase()}</a>)}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ResultsLog() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/results", { params: { q } }).then(({ data }) => setRows(data)); }, [q]);
  return (
    <div className="px-10 py-8 max-w-[1400px] space-y-5 fade-in" data-testid="results-log-page">
      <div className="flex items-end gap-4">
        <div className="flex-1"><h1 className="text-2xl font-bold font-display">Results log</h1>
          <p className="text-sm text-slate-400">Every finished client resume, saved at approval with its scores and keywords.</p></div>
        <input data-testid="results-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search client"
          className="bg-slate-900 border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-blue-500" />
      </div>
      {rows && !rows.length && <div className="card-surface text-sm text-slate-400" data-testid="results-empty">No finished resumes yet. They appear here when the client approval is recorded.</div>}
      {rows?.map((r) => <Row key={r.job_id} r={r} />)}
    </div>
  );
}
